import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Car,
  CheckCircle2,
  ChevronRight,
  GripVertical,
  Plus,
  User,
} from "lucide-react";
import * as React from "react";

import { Avatar, AvatarFallback } from "@/components/avatar";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/dialog";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Layout } from "@/components/layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Textarea } from "@/components/textarea";
import {
  type Customer,
  type Deal,
  type DealStage,
  formatCurrency,
  getInitials,
  getStageDisplayName,
  type Vehicle,
} from "@/lib/types";

const STAGES: DealStage[] = [
  "new_lead",
  "contacted",
  "test_drive",
  "negotiation",
  "closed_won",
  "closed_lost",
];

const stageColors: Record<DealStage, { bg: string; border: string; text: string; icon: string }> = {
  new_lead: {
    bg: "bg-slate-50",
    border: "border-l-slate-400",
    text: "text-slate-600",
    icon: "bg-slate-100",
  },
  contacted: {
    bg: "bg-indigo-50",
    border: "border-l-indigo-500",
    text: "text-indigo-700",
    icon: "bg-indigo-100",
  },
  test_drive: {
    bg: "bg-violet-50",
    border: "border-l-violet-500",
    text: "text-violet-700",
    icon: "bg-violet-100",
  },
  negotiation: {
    bg: "bg-amber-50",
    border: "border-l-amber-500",
    text: "text-amber-700",
    icon: "bg-amber-100",
  },
  closed_won: {
    bg: "bg-emerald-50",
    border: "border-l-emerald-500",
    text: "text-emerald-700",
    icon: "bg-emerald-100",
  },
  closed_lost: {
    bg: "bg-red-50",
    border: "border-l-red-500",
    text: "text-red-700",
    icon: "bg-red-100",
  },
};

// Color palette for avatar backgrounds - Fresh design
const avatarColors = [
  "bg-indigo-100 text-indigo-700",
  "bg-violet-100 text-violet-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-rose-100 text-rose-700",
  "bg-cyan-100 text-cyan-700",
];

function getAvatarColor(name: string): string {
  const index =
    name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % avatarColors.length;
  return avatarColors[index];
}

const emptyDeal = {
  customer_id: "",
  vehicle_id: "",
  stage: "new_lead" as DealStage,
  value: "",
  notes: "",
};

export function Pipeline() {
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [formData, setFormData] = React.useState(emptyDeal);

  // Close deal dialog state
  const [isCloseDealDialogOpen, setIsCloseDealDialogOpen] = React.useState(false);
  const [pendingCloseDeal, setPendingCloseDeal] = React.useState<Deal | null>(null);
  const [finalPrice, setFinalPrice] = React.useState("");

  // Fetch deals
  const { data: deals = [], isLoading } = useQuery<Deal[]>({
    queryKey: ["deals"],
    queryFn: async () => {
      const res = await fetch("/api/deals");
      if (!res.ok) throw new Error("Failed to fetch deals");
      return res.json();
    },
  });

  // Fetch customers for dropdown
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: async () => {
      const res = await fetch("/api/customers");
      if (!res.ok) throw new Error("Failed to fetch customers");
      return res.json();
    },
  });

  // Fetch vehicles for dropdown
  const { data: vehicles = [] } = useQuery<Vehicle[]>({
    queryKey: ["vehicles"],
    queryFn: async () => {
      const res = await fetch("/api/vehicles");
      if (!res.ok) throw new Error("Failed to fetch vehicles");
      return res.json();
    },
  });

  // Create deal mutation
  const createDeal = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetch("/api/deals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_id: parseInt(data.customer_id),
          vehicle_id: data.vehicle_id ? parseInt(data.vehicle_id) : null,
          stage: data.stage,
          value: data.value ? parseInt(data.value) : null,
          notes: data.notes || null,
        }),
      });
      if (!res.ok) throw new Error("Failed to create deal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setIsAddDialogOpen(false);
      setFormData(emptyDeal);
    },
  });

  // Update deal stage mutation (for non-closed_won stages)
  const updateDealStage = useMutation({
    mutationFn: async ({ id, stage }: { id: number; stage: DealStage }) => {
      const res = await fetch(`/api/deals/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage }),
      });
      if (!res.ok) throw new Error("Failed to update deal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // Close deal as won mutation (with final price and vehicle marking)
  const closeDealAsWon = useMutation({
    mutationFn: async ({ id, value }: { id: number; value: number }) => {
      const res = await fetch(`/api/deals/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage: "closed_won", value, markVehicleSold: true }),
      });
      if (!res.ok) throw new Error("Failed to close deal");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      setIsCloseDealDialogOpen(false);
      setPendingCloseDeal(null);
      setFinalPrice("");
    },
  });

  // Group deals by stage
  const dealsByStage = STAGES.reduce(
    (acc, stage) => {
      acc[stage] = deals.filter((deal) => deal.stage === stage);
      return acc;
    },
    {} as Record<DealStage, Deal[]>
  );

  // Calculate stage totals
  const stageTotals = STAGES.reduce(
    (acc, stage) => {
      acc[stage] = dealsByStage[stage].reduce((sum, deal) => sum + (deal.value || 0), 0);
      return acc;
    },
    {} as Record<DealStage, number>
  );

  // Handle drag and drop
  const handleDragStart = (e: React.DragEvent, dealId: number) => {
    e.dataTransfer.setData("dealId", dealId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, stage: DealStage) => {
    e.preventDefault();
    const dealId = parseInt(e.dataTransfer.getData("dealId"));
    const deal = deals.find((d) => d.id === dealId);

    // If moving to closed_won, show confirmation dialog
    if (stage === "closed_won" && deal) {
      setPendingCloseDeal(deal);
      setFinalPrice(deal.value?.toString() || deal.vehicle?.price?.toString() || "");
      setIsCloseDealDialogOpen(true);
      return;
    }

    // Otherwise, update stage normally
    updateDealStage.mutate({ id: dealId, stage });
  };

  // Handle close deal confirmation
  const handleConfirmCloseDeal = () => {
    if (!pendingCloseDeal || !finalPrice) return;
    closeDealAsWon.mutate({
      id: pendingCloseDeal.id,
      value: parseInt(finalPrice),
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">Sales Pipeline</h1>
            <p className="mt-1 text-slate-500">ติดตามดีลในทุกขั้นตอนการขาย</p>
          </div>
          <Button
            className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
            onClick={() => setIsAddDialogOpen(true)}
          >
            <Plus className="mr-2 size-4" />
            ดีลใหม่
          </Button>
        </div>

        {/* Pipeline Summary */}
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {STAGES.filter((s) => s !== "closed_lost").map((stage) => (
            <Card
              key={stage}
              className={`rounded-2xl border-l-4 border-slate-200 shadow-sm ${stageColors[stage].border}`}
            >
              <CardContent className="p-4">
                <p
                  className={`text-xs font-semibold uppercase tracking-wide ${stageColors[stage].text}`}
                >
                  {getStageDisplayName(stage)}
                </p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {dealsByStage[stage].length} ดีล
                </p>
                <p className="text-sm text-slate-500">{formatCurrency(stageTotals[stage])}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pipeline Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex min-w-max gap-4">
            {STAGES.map((stage) => (
              <div
                key={stage}
                className="w-72 flex-shrink-0"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage)}
              >
                <Card className="h-full rounded-2xl border-slate-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle
                        className={`text-sm font-semibold uppercase tracking-wide ${stageColors[stage].text}`}
                      >
                        {getStageDisplayName(stage)}
                      </CardTitle>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                        {dealsByStage[stage].length}
                      </span>
                    </div>
                    {stageTotals[stage] > 0 && (
                      <p className="text-xs text-slate-500">{formatCurrency(stageTotals[stage])}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isLoading ? (
                      <div className="space-y-3">
                        {[1, 2].map((i) => (
                          <div key={i} className="animate-pulse rounded-xl bg-slate-50 p-3">
                            <div className="h-4 w-24 rounded bg-slate-100" />
                            <div className="mt-2 h-3 w-32 rounded bg-slate-100" />
                          </div>
                        ))}
                      </div>
                    ) : dealsByStage[stage].length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <p className="text-xs text-slate-400">ไม่มีดีลในขั้นตอนนี้</p>
                      </div>
                    ) : (
                      dealsByStage[stage].map((deal) => (
                        <div
                          key={deal.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, deal.id)}
                          className={`cursor-grab rounded-xl border border-slate-200 bg-white p-3 transition-all hover:border-indigo-200 hover:shadow-md active:cursor-grabbing`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <GripVertical className="size-4 text-slate-300" />
                              {deal.customer ? (
                                <Avatar className="size-8">
                                  <AvatarFallback className={getAvatarColor(deal.customer.name)}>
                                    {getInitials(deal.customer.name)}
                                  </AvatarFallback>
                                </Avatar>
                              ) : (
                                <div className="flex size-8 items-center justify-center rounded-full bg-slate-100">
                                  <User className="size-4 text-slate-400" />
                                </div>
                              )}
                            </div>
                            {deal.value && (
                              <span className="text-xs font-bold text-indigo-600">
                                {formatCurrency(deal.value)}
                              </span>
                            )}
                          </div>
                          <div className="mt-2">
                            <p className="text-sm font-medium text-slate-900">
                              {deal.customer?.name || "ลูกค้าไม่ทราบชื่อ"}
                            </p>
                            {deal.vehicle && (
                              <p className="flex items-center gap-1 text-xs text-slate-500">
                                <Car className="size-3" />
                                {deal.vehicle.year} {deal.vehicle.make} {deal.vehicle.model}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Stage Navigation Arrows (for small screens) */}
        <div className="flex items-center justify-center gap-2 text-sm text-slate-400 lg:hidden">
          <ChevronRight className="size-4" />
          <span>เลื่อนเพื่อดูทุกขั้นตอน</span>
          <ChevronRight className="size-4" />
        </div>
      </div>

      {/* Add Deal Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>สร้างดีลใหม่</DialogTitle>
            <DialogDescription>เพิ่มดีลใหม่เข้าสู่ Sales Pipeline</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createDeal.mutate(formData);
            }}
            className="space-y-4"
          >
            <div className="space-y-4">
              <div>
                <Label htmlFor="customer">ลูกค้า *</Label>
                <Select
                  value={formData.customer_id}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, customer_id: value })
                  }
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="เลือกลูกค้า" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="vehicle">รถยนต์ (ไม่บังคับ)</Label>
                <Select
                  value={formData.vehicle_id || "none"}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, vehicle_id: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue placeholder="เลือกรถยนต์" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">ไม่ระบุรถยนต์</SelectItem>
                    {vehicles
                      .filter((v) => v.status === "available")
                      .map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id.toString()}>
                          {vehicle.year} {vehicle.make} {vehicle.model} -{" "}
                          {formatCurrency(vehicle.price)}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="stage">ขั้นตอน</Label>
                <Select
                  value={formData.stage}
                  onValueChange={(value: DealStage) => setFormData({ ...formData, stage: value })}
                >
                  <SelectTrigger className="rounded-xl border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {getStageDisplayName(stage)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="value">มูลค่าดีล</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">฿</span>
                  <Input
                    id="value"
                    type="number"
                    placeholder="850000"
                    className="rounded-xl border-slate-200 pl-8"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">หมายเหตุ</Label>
                <Textarea
                  id="notes"
                  placeholder="รายละเอียดเพิ่มเติม..."
                  className="rounded-xl border-slate-200"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                onClick={() => setIsAddDialogOpen(false)}
              >
                ยกเลิก
              </Button>
              <Button
                type="submit"
                className="rounded-xl bg-indigo-500 hover:bg-indigo-600"
                disabled={!formData.customer_id || createDeal.isPending}
              >
                {createDeal.isPending ? "กำลังสร้าง..." : "สร้างดีล"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close Deal Confirmation Dialog */}
      <Dialog
        open={isCloseDealDialogOpen}
        onOpenChange={(open) => {
          setIsCloseDealDialogOpen(open);
          if (!open) {
            setPendingCloseDeal(null);
            setFinalPrice("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex size-10 items-center justify-center rounded-xl bg-emerald-100">
                <CheckCircle2 className="size-5 text-emerald-600" />
              </div>
              <div>
                <DialogTitle>ปิดดีลสำเร็จ</DialogTitle>
                <DialogDescription>ยืนยันรายละเอียดการขาย</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {/* Deal Summary */}
            <div className="space-y-2 rounded-xl bg-slate-50 p-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">ลูกค้า</span>
                <span className="font-medium text-slate-900">
                  {pendingCloseDeal?.customer?.name || "ลูกค้าไม่ทราบชื่อ"}
                </span>
              </div>
              {pendingCloseDeal?.vehicle && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">รถยนต์</span>
                  <span className="font-medium text-slate-900">
                    {pendingCloseDeal.vehicle.year} {pendingCloseDeal.vehicle.make}{" "}
                    {pendingCloseDeal.vehicle.model}
                  </span>
                </div>
              )}
            </div>

            {/* Final Price Input */}
            <div>
              <Label htmlFor="finalPrice">ราคาขายสุดท้าย *</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">฿</span>
                <Input
                  id="finalPrice"
                  type="number"
                  placeholder="850000"
                  className="rounded-xl border-slate-200 pl-8"
                  value={finalPrice}
                  onChange={(e) => setFinalPrice(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Vehicle Warning */}
            {pendingCloseDeal?.vehicle && (
              <div className="flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-700">
                <AlertTriangle className="size-4 flex-shrink-0" />
                <span>
                  รถยนต์จะถูกเปลี่ยนสถานะเป็น <strong>ขายแล้ว</strong> อัตโนมัติ
                </span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-xl"
              onClick={() => {
                setIsCloseDealDialogOpen(false);
                setPendingCloseDeal(null);
                setFinalPrice("");
              }}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleConfirmCloseDeal}
              disabled={!finalPrice || closeDealAsWon.isPending}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700"
            >
              {closeDealAsWon.isPending ? "กำลังปิดดีล..." : "ยืนยันการขาย"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
